import { BasicAuthServiceHelper } from '@/modules/Auth'
import { InsurancesTypes } from '@/modules/Insurances'
import { Insurance, PartnerQuote, PartnerQuoteProspect, PartnerWebContent } from '@/data'
import { AppDispatch } from '@/store'
import {
  Attempts,
  GetInsurances,
  GetPartnersQuote,
  PostPartnerQuote,
  WebContents,
  CheckInsurance,
  SubscribeInsurance,
} from './apiTypes'

/* *******************************
  GET INSURANCES
******************************* */

export const getInsurancesAsync = async (isOwner: boolean): Promise<Insurance[]> => {
  const { items: insurances } = await BasicAuthServiceHelper.getAllHydraCollectionPagesV2Async<GetInsurances.Response>(
    isOwner ? GetInsurances.OwnerRoute : GetInsurances.BorrowerRoute,
  )

  return insurances
}

export const getInsurancesPremiumAsync = async (): Promise<Insurance[]> => {
  const { items: insurances } = await BasicAuthServiceHelper.getAllHydraCollectionPagesV2Async<GetInsurances.Response>(
    GetInsurances.PremiumRoute,
  )

  return insurances
}

/* *******************************
  CHECK INSURANCE
******************************* */

export const checkInsuranceAsync =
  () =>
  async (insurance: string, identifier: string): Promise<void> => {
    await BasicAuthServiceHelper.requestAuthenticatedV2Async<CheckInsurance.Response, CheckInsurance.Body>(
      CheckInsurance.Route,
      { body: { insurance, identifier } },
    )
  }

/* *******************************
  SUBSCRIBE INSURANCE
******************************* */

export const subscribeInsuranceAsync =
  () =>
  async (insurance: string, identifier: string): Promise<void> => {
    await BasicAuthServiceHelper.requestAuthenticatedV2Async<SubscribeInsurance.Response, SubscribeInsurance.Body>(
      SubscribeInsurance.Route,
      { body: { insurance, identifier } },
    )
  }

/* *******************************
  GET PARTNER QUOTE
******************************* */

export const getPartnerQuote = async (): Promise<PartnerQuote | null> => {
  const response = await BasicAuthServiceHelper.getAllHydraCollectionPagesV2Async<GetPartnersQuote.Response>(
    GetPartnersQuote.Route,
  )

  if (response.items?.length) return response.items[0]

  return null
}

export const postProspectQuote = async (partner: string): Promise<PartnerQuoteProspect> => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<PostPartnerQuote.Response, PostPartnerQuote.Body>(
    PostPartnerQuote.Route,
    { body: { partner } },
  )
}

// Web content
export const getPartnerQuoteWebContent = async (IRI: string): Promise<PartnerWebContent> => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<WebContents.Response>(
    WebContents.GetPartnerQuoteRoute(IRI),
  )
}

/* *******************************
  GET HELPER CONTENT
******************************* */

// import helperMatmutStub from './stubs/helperMatmutStub'

export const getHelperWebContentAsync = async (insuranceSlug: string): Promise<PartnerWebContent> => {
  // return helperMatmutStub

  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<WebContents.Response>(
    WebContents.GetHelperRoute(insuranceSlug),
  )
}

/* *******************************
  GET REFUSED CONTENT
******************************* */

// import refusedMatmutStub from './stubs/refusedMatmutStub'

export const getRefusedWebContentAsync = async (insuranceSlug: string): Promise<PartnerWebContent> => {
  // return refusedMatmutStub

  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<WebContents.Response>(
    WebContents.GetRefusedRoute(insuranceSlug),
  )
}

export const getSuccessWebContentAsync = async (IRI: string): Promise<PartnerWebContent> => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<WebContents.Response>(
    WebContents.GetSuccessRoute(IRI),
  )
}

/* *******************************
  ATTEMPTS
******************************* */

export const dispatchGetInsuranceAttemptsAsync =
  (dispatch: AppDispatch) =>
  async (insuranceId: string): Promise<number> => {
    const { attempt = 0 } = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
      Attempts.GetResponse,
      never,
      Attempts.GetUrlParams
    >(Attempts.GetRoute, { urlPathParams: { insuranceId } })

    dispatch({ type: InsurancesTypes.SetInsuranceAttempt, payload: { insuranceId, attempt } })

    return attempt
  }

export const dispatchDecrementInsuranceAttemptsAsync =
  (dispatch: AppDispatch) =>
  async (insuranceId: string): Promise<number> => {
    const { attempt = 0 } = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
      Attempts.DecrementResponse,
      never,
      Attempts.DecrementUrlParams
    >(Attempts.GetRoute, { urlPathParams: { insuranceId } })

    dispatch({ type: InsurancesTypes.SetInsuranceAttempt, payload: { insuranceId, attempt } })

    return attempt
  }
